import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { FormFieldWrapper } from "@/components/ui/form-field-wrapper";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SuggestionList } from "@/components/ui/suggestion-list";
import { useAppForm } from "@/components/ui/tanstack-form";
import {
  useHeraConfiguration,
  useUpdateHeraConfiguration,
} from "@/hooks/use-hera-configuration";
import { usePlacesAutocomplete } from "@/hooks/use-places-autocomplete";
import { parseGoogleAddressComponents } from "@/lib/parse-google-address-components";
import { AddressSchema } from "@/types/address";
import { useEffect, useState } from "react";
import { z } from "zod";

const dollarAmountSchema = z
  .string()
  .transform((val) => val.trim() || "")
  .refine(
    (val) =>
      val === "" ||
      (!Number.isNaN(Number.parseFloat(val)) && Number.parseFloat(val) >= 0),
    "Must be a valid dollar amount",
  );

const HeraConfigurationSchema = z.object({
  address: AddressSchema,
  support_email: z.string().email("Please enter a valid support email address"),
  support_phone: z.string().min(1, "Support phone number is required"),
  sales_email: z.string().email("Please enter a valid sales email address"),
  sales_phone: z.string().min(1, "Sales phone number is required"),
  check_processing_fee: dollarAmountSchema,
  /** Comma-separated BCC list for registrant and title-requestor email. */
  global_mail_cc: z.string(),
  needs_attention_send_to_registrant: z.boolean(),
});

export interface HeraConfigurationFormProps {
  /** When true, configuration is fetched and form is active (e.g. tab is selected or panel is open). */
  isActive: boolean;
  /** Called after save succeeds (e.g. close panel, show toast). */
  onSaveSuccess?: () => void;
  /** Called when user clicks Cancel (e.g. close panel). */
  onCancel?: () => void;
}

export function HeraConfigurationForm({
  isActive,
  onSaveSuccess,
  onCancel: _onCancel,
}: HeraConfigurationFormProps) {
  const [placesAutoCompleteInput, setPlacesAutoCompleteInput] = useState("");
  const [originalFormData, setOriginalFormData] = useState<{
    address: {
      address1: string;
      address2: string;
      city: string;
      state: string;
      zipcode: string;
    };
    support_email: string;
    support_phone: string;
    sales_email: string;
    sales_phone: string;
    check_processing_fee: string;
    global_mail_cc: string;
    needs_attention_send_to_registrant: boolean;
  } | null>(null);

  const { data: configData, isLoading, error } = useHeraConfiguration(isActive);
  const updateConfiguration = useUpdateHeraConfiguration();

  const { suggestions } = usePlacesAutocomplete(placesAutoCompleteInput, {
    includedRegionCodes: ["us"],
    includedPrimaryTypes: ["street_address"],
  });

  const form = useAppForm({
    defaultValues: {
      address: {
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipcode: "",
      },
      support_email: "",
      support_phone: "",
      sales_email: "",
      sales_phone: "",
      check_processing_fee: "",
      global_mail_cc: "",
      needs_attention_send_to_registrant: false,
    },
    validators: {
      onSubmit: HeraConfigurationSchema,
    },
    onSubmit: async ({ value }) => {
      await updateConfiguration.mutateAsync({
        address_line1: value.address.address1,
        address_line2: value.address.address2,
        city: value.address.city,
        state: value.address.state,
        zipcode: value.address.zipcode,
        support_email: value.support_email,
        support_phone: value.support_phone,
        sales_email: value.sales_email,
        sales_phone: value.sales_phone,
        // Keep legacy keys synchronized for existing backend/report consumers.
        email: value.support_email,
        phone: value.support_phone,
        check_processing_fee: value.check_processing_fee || null,
        global_mail_cc: value.global_mail_cc?.trim()
          ? value.global_mail_cc.trim()
          : null,
        needs_attention_send_to_registrant:
          value.needs_attention_send_to_registrant,
      });
      onSaveSuccess?.();
    },
  });

  useEffect(() => {
    if (configData) {
      const formData = {
        address: {
          address1: configData.address_line1 || "",
          address2: configData.address_line2 || "",
          city: configData.city || "",
          state: configData.state || "",
          zipcode: configData.zipcode || "",
        },
        support_email: configData.support_email || configData.email || "",
        support_phone: configData.support_phone || configData.phone || "",
        sales_email: configData.sales_email || "",
        sales_phone: configData.sales_phone || "",
        check_processing_fee: configData.check_processing_fee || "",
        global_mail_cc: configData.global_mail_cc ?? "",
        needs_attention_send_to_registrant: Boolean(
          configData.needs_attention_send_to_registrant,
        ),
      };

      form.setFieldValue("address.address1", formData.address.address1);
      form.setFieldValue("address.address2", formData.address.address2);
      form.setFieldValue("address.city", formData.address.city);
      form.setFieldValue("address.state", formData.address.state);
      form.setFieldValue("address.zipcode", formData.address.zipcode);
      form.setFieldValue("support_email", formData.support_email);
      form.setFieldValue("support_phone", formData.support_phone);
      form.setFieldValue("sales_email", formData.sales_email);
      form.setFieldValue("sales_phone", formData.sales_phone);
      form.setFieldValue("check_processing_fee", formData.check_processing_fee);
      form.setFieldValue("global_mail_cc", formData.global_mail_cc);
      form.setFieldValue(
        "needs_attention_send_to_registrant",
        formData.needs_attention_send_to_registrant,
      );

      setTimeout(() => {
        form.validateField("address.address1", "change");
        form.validateField("address.address2", "change");
        form.validateField("address.city", "change");
        form.validateField("address.state", "change");
        form.validateField("address.zipcode", "change");
        form.validateField("support_email", "change");
        form.validateField("support_phone", "change");
        form.validateField("sales_email", "change");
        form.validateField("sales_phone", "change");
        form.validateField("check_processing_fee", "change");
        form.validateField("global_mail_cc", "change");
        form.validateField("needs_attention_send_to_registrant", "change");
      }, 0);

      setOriginalFormData(formData);
    }
  }, [configData, form]);

  const revertAddress = () => {
    if (originalFormData) {
      form.setFieldValue("address.address1", originalFormData.address.address1);
      form.setFieldValue("address.address2", originalFormData.address.address2);
      form.setFieldValue("address.city", originalFormData.address.city);
      form.setFieldValue("address.state", originalFormData.address.state);
      form.setFieldValue("address.zipcode", originalFormData.address.zipcode);
      setTimeout(() => {
        form.validateField("address.address1", "change");
        form.validateField("address.address2", "change");
        form.validateField("address.city", "change");
        form.validateField("address.state", "change");
        form.validateField("address.zipcode", "change");
      }, 0);
    }
  };

  const revertContact = () => {
    if (originalFormData) {
      form.setFieldValue("support_phone", originalFormData.support_phone);
      form.setFieldValue("sales_phone", originalFormData.sales_phone);
      setTimeout(() => {
        form.validateField("support_phone", "change");
        form.validateField("sales_phone", "change");
      }, 0);
    }
  };

  const revertEmail = () => {
    if (originalFormData) {
      form.setFieldValue("support_email", originalFormData.support_email);
      form.setFieldValue("sales_email", originalFormData.sales_email);
      form.setFieldValue(
        "global_mail_cc",
        originalFormData.global_mail_cc ?? "",
      );
      form.setFieldValue(
        "needs_attention_send_to_registrant",
        originalFormData.needs_attention_send_to_registrant ?? false,
      );
      setTimeout(() => {
        form.validateField("support_email", "change");
        form.validateField("sales_email", "change");
        form.validateField("global_mail_cc", "change");
        form.validateField("needs_attention_send_to_registrant", "change");
      }, 0);
    }
  };

  const revertFees = () => {
    if (originalFormData) {
      form.setFieldValue(
        "check_processing_fee",
        originalFormData.check_processing_fee ?? "",
      );
      setTimeout(() => {
        form.validateField("check_processing_fee", "change");
      }, 0);
    }
  };

  const handleSuggestionClick = async (
    suggestion: google.maps.places.AutocompleteSuggestion,
  ) => {
    const place = suggestion.placePrediction?.toPlace();
    if (!place) {
      return;
    }
    await place.fetchFields({
      fields: ["addressComponents"],
    });

    if (!place?.addressComponents) {
      return;
    }

    const { address1, address2, city, state, zipcode } =
      parseGoogleAddressComponents(place.addressComponents);

    form.setFieldValue("address.address1", address1);
    form.setFieldValue("address.address2", address2);
    form.setFieldValue("address.city", city);
    form.setFieldValue("address.state", state);
    form.setFieldValue("address.zipcode", zipcode);

    setTimeout(() => {
      form.validateField("address.address1", "change");
      form.validateField("address.address2", "change");
      form.validateField("address.city", "change");
      form.validateField("address.state", "change");
      form.validateField("address.zipcode", "change");
    }, 0);

    setPlacesAutoCompleteInput("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  };

  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="flex justify-center items-center py-8">
          <div className="text-sm text-muted-foreground">
            Loading configuration...
          </div>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className="text-sm text-destructive">
            Failed to load configuration. Please try again.
          </div>
        </div>
      )}

      {updateConfiguration.error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className="text-sm text-destructive">
            Failed to save configuration. Please try again.
          </div>
        </div>
      )}

      <form.AppForm>
        <form onSubmit={handleSubmit} className="space-y-6">
          <form.Subscribe
            selector={(state) => ({
              values: state.values,
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ values, canSubmit, isSubmitting }) => {
              const orig = originalFormData;
              const isAddressDirty =
                !!orig &&
                (values.address?.address1 !== orig.address.address1 ||
                  values.address?.address2 !== orig.address.address2 ||
                  values.address?.city !== orig.address.city ||
                  values.address?.state !== orig.address.state ||
                  values.address?.zipcode !== orig.address.zipcode);
              const isContactDirty =
                !!orig &&
                (values.support_phone !== orig.support_phone ||
                  values.sales_phone !== orig.sales_phone);
              const isEmailDirty =
                !!orig &&
                (values.support_email !== orig.support_email ||
                  values.sales_email !== orig.sales_email ||
                  (values.global_mail_cc ?? "").trim() !==
                    (orig.global_mail_cc ?? "").trim() ||
                  Boolean(values.needs_attention_send_to_registrant) !==
                    Boolean(orig.needs_attention_send_to_registrant));
              const isFeesDirty =
                !!orig &&
                (values.check_processing_fee ?? "") !==
                  (orig.check_processing_fee ?? "");

              return (
                <>
                  {/* Address Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Address</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <form.AppField
                        name="address.address1"
                        validators={{
                          onChange: AddressSchema.shape.address1,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Address Line 1"
                          >
                            <SuggestionList
                              closeOnBlur
                              suggestions={suggestions}
                              renderSuggestion={(suggestion) => (
                                <span>
                                  {suggestion.placePrediction?.text?.text}
                                </span>
                              )}
                              getSuggestionKey={(suggestion) =>
                                suggestion.placePrediction?.placeId ?? ""
                              }
                              onSuggestionSelect={handleSuggestionClick}
                            >
                              <Input
                                id={field.name}
                                placeholder="Enter street address"
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e.target.value);
                                  setPlacesAutoCompleteInput(e.target.value);
                                }}
                                onBlur={field.handleBlur}
                              />
                            </SuggestionList>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>

                      <form.AppField name="address.address2">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Address Line 2"
                          >
                            <Input
                              id={field.name}
                              placeholder="Apartment, suite, etc. (optional)"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>

                      <div className="grid grid-cols-2 gap-4">
                        <form.AppField
                          name="address.city"
                          validators={{
                            onChange: AddressSchema.shape.city,
                          }}
                        >
                          {(field) => (
                            <FormFieldWrapper field={field} label="City">
                              <Input
                                id={field.name}
                                placeholder="City"
                                value={field.state.value}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                onBlur={field.handleBlur}
                              />
                            </FormFieldWrapper>
                          )}
                        </form.AppField>

                        <form.AppField
                          name="address.state"
                          validators={{
                            onChange: AddressSchema.shape.state,
                          }}
                        >
                          {(field) => (
                            <FormFieldWrapper field={field} label="State">
                              <Input
                                id={field.name}
                                placeholder="State"
                                value={field.state.value}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                onBlur={field.handleBlur}
                              />
                            </FormFieldWrapper>
                          )}
                        </form.AppField>
                      </div>

                      <form.AppField
                        name="address.zipcode"
                        validators={{
                          onChange: AddressSchema.shape.zipcode,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper field={field} label="ZIP Code">
                            <Input
                              id={field.name}
                              placeholder="ZIP Code"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>

                      <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={revertAddress}
                          disabled={!isAddressDirty}
                        >
                          Revert
                        </Button>
                        <Button
                          type="submit"
                          size="sm"
                          disabled={
                            !isAddressDirty ||
                            !canSubmit ||
                            isSubmitting ||
                            updateConfiguration.isPending ||
                            isLoading
                          }
                        >
                          {isSubmitting || updateConfiguration.isPending
                            ? "Saving..."
                            : "Save"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Contact Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Contact Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <form.AppField
                        name="support_phone"
                        validators={{
                          onChange: HeraConfigurationSchema.shape.support_phone,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper field={field} label="Support Phone">
                            <Input
                              id={field.name}
                              type="tel"
                              placeholder="Enter support phone number"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>

                      <form.AppField
                        name="sales_phone"
                        validators={{
                          onChange: HeraConfigurationSchema.shape.sales_phone,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper field={field} label="Sales Phone">
                            <Input
                              id={field.name}
                              type="tel"
                              placeholder="Enter sales phone number"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>

                      <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={revertContact}
                          disabled={!isContactDirty}
                        >
                          Revert
                        </Button>
                        <Button
                          type="submit"
                          size="sm"
                          disabled={
                            !isContactDirty ||
                            !canSubmit ||
                            isSubmitting ||
                            updateConfiguration.isPending ||
                            isLoading
                          }
                        >
                          {isSubmitting || updateConfiguration.isPending
                            ? "Saving..."
                            : "Save"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Email Configuration */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Email Configuration
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <form.AppField
                        name="support_email"
                        validators={{
                          onChange: HeraConfigurationSchema.shape.support_email,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Support Email Address"
                          >
                            <Input
                              id={field.name}
                              type="email"
                              placeholder="Enter support email address"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>

                      <form.AppField
                        name="sales_email"
                        validators={{
                          onChange: HeraConfigurationSchema.shape.sales_email,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Sales Email Address"
                          >
                            <Input
                              id={field.name}
                              type="email"
                              placeholder="Enter sales email address"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>

                      <form.AppField
                        name="global_mail_cc"
                        validators={{
                          onChange:
                            HeraConfigurationSchema.shape.global_mail_cc,
                        }}
                      >
                        {(field) => (
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">
                              Comma-separated addresses BCC&apos;d on email to a
                              registrant or title requestor, including invoices,
                              comments, title requests, and scheduled
                              notifications (Mailgun). Recipients cannot see
                              these addresses.
                            </p>
                            <FormFieldWrapper
                              field={field}
                              label="Global notification BCC (optional)"
                            >
                              <Input
                                id={field.name}
                                type="text"
                                placeholder="billing@example.com, ops@example.com"
                                value={field.state.value}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                onBlur={field.handleBlur}
                                autoComplete="off"
                              />
                            </FormFieldWrapper>
                          </div>
                        )}
                      </form.AppField>

                      <form.AppField
                        name="needs_attention_send_to_registrant"
                        validators={{
                          onChange:
                            HeraConfigurationSchema.shape
                              .needs_attention_send_to_registrant,
                        }}
                      >
                        {(field) => (
                          <div className="flex items-start gap-2">
                            <Checkbox
                              id={field.name}
                              checked={field.state.value}
                              onCheckedChange={(checked) =>
                                field.handleChange(checked === true)
                              }
                            />
                            <div className="space-y-1">
                              <Label htmlFor={field.name}>
                                Send needs-attention emails to the registrant
                              </Label>
                              <p className="text-xs text-muted-foreground">
                                When off, daily needs-attention emails go only
                                to Global notification BCC. When on, they go to
                                the registrant and BCC that list.
                              </p>
                            </div>
                          </div>
                        )}
                      </form.AppField>

                      <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={revertEmail}
                          disabled={!isEmailDirty}
                        >
                          Revert
                        </Button>
                        <Button
                          type="submit"
                          size="sm"
                          disabled={
                            !isEmailDirty ||
                            !canSubmit ||
                            isSubmitting ||
                            updateConfiguration.isPending ||
                            isLoading
                          }
                        >
                          {isSubmitting || updateConfiguration.isPending
                            ? "Saving..."
                            : "Save"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Fees */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Fees</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <form.AppField
                        name="check_processing_fee"
                        validators={{
                          onChange:
                            HeraConfigurationSchema.shape.check_processing_fee,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Check Processing Fee ($)"
                          >
                            <Input
                              id={field.name}
                              type="text"
                              inputMode="decimal"
                              placeholder="0.00"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>

                      <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={revertFees}
                          disabled={!isFeesDirty}
                        >
                          Revert
                        </Button>
                        <Button
                          type="submit"
                          size="sm"
                          disabled={
                            !isFeesDirty ||
                            !canSubmit ||
                            isSubmitting ||
                            updateConfiguration.isPending ||
                            isLoading
                          }
                        >
                          {isSubmitting || updateConfiguration.isPending
                            ? "Saving..."
                            : "Save"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </>
              );
            }}
          </form.Subscribe>
        </form>
      </form.AppForm>
    </div>
  );
}
